import type { ValueOf } from '#layers/base/public';

/** Declares the view modes in the order the view picker lists them. */
export const CalendarViewModes = {
	Year: 'year',
	Month: 'month',
	Week: 'week',
	Day: 'day',
	List: 'list',
} as const;

export type CalendarViewMode = ValueOf<typeof CalendarViewModes>;

const ScheduleXViews = {
	MonthGrid: 'month-grid',
	Week: 'week',
	Day: 'day',
} as const;

export type ScheduleXView = ValueOf<typeof ScheduleXViews>;

export const CalendarNavigations = {
	Previous: 'previous',
	Next: 'next',
	Today: 'today',
} as const;

export type CalendarNavigation = ValueOf<typeof CalendarNavigations>;

/** Uses `Temporal` duration field names, so `add({ [step]: 1 })` moves one step. */
const CalendarStepUnits = {
	Years: 'years',
	Months: 'months',
	Weeks: 'weeks',
	Days: 'days',
} as const;

interface CalendarViewDefinition {
	/** Names the schedule-x view that draws this mode, or that stays hidden behind a view Monospace draws. */
	scheduleXView: ScheduleXView;
	step: ValueOf<typeof CalendarStepUnits>;
	/** Sets how many months Monospace draws itself; null leaves the view to schedule-x. */
	periodMonths: 1 | 12 | null;
}

export const CALENDAR_VIEW_DEFINITIONS = {
	[CalendarViewModes.Year]: { scheduleXView: ScheduleXViews.MonthGrid, step: CalendarStepUnits.Years, periodMonths: 12 },
	[CalendarViewModes.Month]: { scheduleXView: ScheduleXViews.MonthGrid, step: CalendarStepUnits.Months, periodMonths: null },
	[CalendarViewModes.Week]: { scheduleXView: ScheduleXViews.Week, step: CalendarStepUnits.Weeks, periodMonths: null },
	[CalendarViewModes.Day]: { scheduleXView: ScheduleXViews.Day, step: CalendarStepUnits.Days, periodMonths: null },
	[CalendarViewModes.List]: { scheduleXView: ScheduleXViews.MonthGrid, step: CalendarStepUnits.Months, periodMonths: 1 },
} satisfies Record<CalendarViewMode, CalendarViewDefinition>;

/** Names how an item draws: Month dots and bars, Week and Day blocks and all-day strips, and List rows. */
export const CalendarEventVariants = {
	Dot: 'dot',
	Bar: 'bar',
	Block: 'block',
	AllDay: 'all-day',
	Row: 'row',
} as const;

export type CalendarEventVariant = ValueOf<typeof CalendarEventVariants>;

export const CalendarEventTimings = {
	Past: 'past',
	Live: 'live',
	Upcoming: 'upcoming',
} as const;

export type CalendarEventTiming = ValueOf<typeof CalendarEventTimings>;

/** Matches schedule-x's default time grid height, set explicitly so an hour's scroll offset is known. */
export const CALENDAR_TIME_GRID_HEIGHT = 1600;

/** Starts the first band at one item, and each later band at its percentile of the period's busy-day counts. */
export const CALENDAR_DENSITY_BANDS = [
	{ percentile: null, fillClass: 'bg-(--cal-density-1)' },
	{ percentile: 0.5, fillClass: 'bg-(--cal-density-2)' },
	{ percentile: 0.8, fillClass: 'bg-(--cal-density-3)' },
	{ percentile: 0.95, fillClass: 'bg-(--cal-density-4)' },
] as const;

export type CalendarDensityBand = typeof CALENDAR_DENSITY_BANDS[number];

export const CALENDAR_DAY_LABEL_FORMAT = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' } as const satisfies Intl.DateTimeFormatOptions;

/** Caps the items loaded per visible range, and the calendar warns when more match. */
export const CALENDAR_PAGE_SIZE = 500;
